import { useEffect, useState } from 'react';

/** Matches a media query and stays in sync with viewport / preference changes. */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    const list = window.matchMedia(query);
    const onChange = (): void => setMatches(list.matches);
    onChange();
    list.addEventListener('change', onChange);
    return () => list.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}

/** True when the visitor has asked the OS to reduce motion. */
export function usePrefersReducedMotion(): boolean {
  return useMediaQuery('(prefers-reduced-motion: reduce)');
}

/**
 * The scroll-scrubbed product story is desktop-only. Tablet and mobile get the
 * simplified stacked layout instead — see ProductJourney.
 */
export function useIsDesktop(): boolean {
  return useMediaQuery('(min-width: 1024px)');
}

/**
 * Fires once when the element first enters the viewport. Used to start demo
 * sequences only when they are actually visible, so nothing animates off-screen.
 */
export function useInViewOnce<T extends Element>(
  ref: React.RefObject<T | null>,
  rootMargin = '-15% 0px',
): boolean {
  const [seen, setSeen] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node || seen) return;
    if (typeof IntersectionObserver === 'undefined') {
      setSeen(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setSeen(true);
          observer.disconnect();
        }
      },
      { rootMargin },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [ref, rootMargin, seen]);

  return seen;
}

/**
 * Steps an index from 0..steps-1 on a fixed interval, once `active` is true and
 * stops at the end. Under reduced motion it jumps straight to the final step so
 * the fully-populated state is what a visitor sees.
 */
export function useSequence(
  steps: number,
  active: boolean,
  intervalMs = 850,
  /** Change this to replay the sequence from the start. */
  runKey = 0,
): number {
  const reduced = usePrefersReducedMotion();
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (!active) return;
    if (reduced) {
      setStep(steps);
      return;
    }
    setStep(0);
    let current = 0;
    const id = window.setInterval(() => {
      current += 1;
      setStep(current);
      if (current >= steps) window.clearInterval(id);
    }, intervalMs);
    return () => window.clearInterval(id);
  }, [active, reduced, steps, intervalMs, runKey]);

  return step;
}
